import { FC, useEffect, useState } from "react"
import { toast } from "react-toastify"
import useError from "../../hooks/useError"
import { ChallengeSectionProps } from "../../pages/Challenge"
import { TasksService } from "../../sdk"
import { useChallenge } from "../../stores/challenge"
import Button from "../Button"
import Card from "../Card"
import EditTaskForm from "../EditTaskForm"
import Icon from "../Icon"
import Modal from "../Modal"
import Row from "../Row"
import AddTaskForm from "../AddTaskForm"
import Message from "../Message"

const ChallengeTasks: FC<ChallengeSectionProps> = ({
  challenge
}) => {
  const tasks = useChallenge(state => state.tasks)
  const setTasks = useChallenge(state => state.setTasks)

  const [loading, setLoading] = useState<boolean>(!tasks)
  const [currentTaskOpen, setCurrentTaskOpen] = useState<number | null>(null)

  const [addTaskModalOpen, setAddTaskModalOpen] = useState<boolean>(false)

  const { handleError } = useError()

  const getTasks = async(): Promise<void> => {
    try {
      const { data } = await TasksService.listChallengeTasks({
        handler: {
          challengeId: challenge.id
        },
        paginator: {
          offset: 0,
          limit: 100
        }
      })
      setTasks(data)
    } catch (err) {
      handleError(err)
    }
  }

  useEffect(() => {
    if (!tasks) {
      void (async(): Promise<void> => {
        await getTasks()
        setLoading(false)
      })()
    }
  }, [])

  return (
    <>
      {loading
        ? <Icon name="spinner" size={40} />
        : tasks && tasks.length > 0
          ?
          <>
            <div className="flex mb-4">
              <h2 className="text-2xl font-semibold">Tasks</h2>
              <Button
                className="ml-4 h-10"
                onClick={(): void => {
                  setAddTaskModalOpen(true)
                }}
              >
                Aggiungi
              </Button>
            </div>

            <div className="grid grid-cols-3 gap-4">
              {
                tasks.map(({ id, title, difficulty, maxScore, pdfUrl, materialsUrl }, i) => {
                  return (
                    <Card heading={title} key={id}>
                      <ul>
                        <Row as="li" className="card__row" justify="between">
                          <div className="card__row-title">Punteggio Massimo</div>

                          <div>{maxScore}</div>
                        </Row>

                        <Row as="li" className="card__row" justify="between">
                          <div className="card__row-title">Difficoltà</div>

                          <div>{difficulty}</div>
                        </Row>

                        {pdfUrl &&
                          <Row as="li" className="card__row" justify="between">
                            <div className="card__row-title">PDF Consegna</div>

                            <a href={pdfUrl} className="text-indigo-700 underline" target="_blank">Visualizza</a>
                          </Row>
                        }

                        {materialsUrl &&
                          <Row as="li" className="card__row" justify="between">
                            <div className="card__row-title">Materiali</div>

                            <a href={materialsUrl} className="text-indigo-700 underline" target="_blank">Visualizza</a>
                          </Row>
                        }
                      </ul>

                      <Row justify="center" className="mt-4">
                        <Button
                          onClick={(): void => {
                            setCurrentTaskOpen(i)
                          }}
                        >
                          Modifica
                        </Button>
                      </Row>
                    </Card>
                  )
                })
              }
            </div>
          </>
          :
          <Message title="Nessuna task trovata" icon="file-off">
            <Button onClick={(): void => setAddTaskModalOpen(true)}>Aggiungi</Button>
          </Message>
      }

      {tasks && currentTaskOpen !== null &&
        <Modal
          close={(): void => setCurrentTaskOpen(null)}
          title="Modifica Task"
        >
          <EditTaskForm
            defaultValues={tasks[currentTaskOpen]}
            onSuccess={async(task): Promise<void> => {
              setTasks(tasks.map(t => t.id !== task.id ? t : task))
              setCurrentTaskOpen(null)
              toast.success("Dati aggiornati correttamente")
            }}
          />
        </Modal>
      }
      {addTaskModalOpen &&
        <Modal
          close={(): void => setAddTaskModalOpen(false)}
          title="Aggiungi Task"
        >
          <AddTaskForm
            onSuccess={async(): Promise<void> => {
              await getTasks()
              setAddTaskModalOpen(false)
              toast.success("Task aggiunta correttamente")
            }}
            challengeId={challenge.id}
          />
        </Modal>
      }
    </>
  )
}

export default ChallengeTasks
